import { Claim, Filters, getClaims } from '@/api/claims'
import { CompaniesListResponse } from '@/api/company'
import { useQueries } from 'react-query'
import { useTranslation } from 'react-i18next'

export type ClaimsLocationGroup = {
  companyId: string
  locationId: string
  locationName: string
  claims: Claim[]
}

type ClaimsMap = Record<string, ClaimsLocationGroup[]>

export const useCompaniesClaims = (
  search: string,
  filters: Filters,
  companies: CompaniesListResponse,
) => {
  const { t } = useTranslation()
  const companyList = companies || []

  const results = useQueries(
    companyList.map((company) => ({
      queryKey: ['claims', company.companyId, search, filters],
      queryFn: async () => {
        const response = await getClaims(company.companyId, search, filters)
        return {
          companyId: company.companyId,
          companyName: company.name,
          claims: response.data as Claim[],
        }
      },
      enabled: !!company.companyId,
    })),
  )

  const claimsMap: ClaimsMap = {}

  results.forEach((result) => {
    if (result.data) {
      const companyKey = `${result.data.companyId}/${result.data.companyName}`

      const locationBuckets: Record<string, ClaimsLocationGroup> = {}

      result.data.claims.forEach((claim) => {
        const locId = claim.location?.location_id || 'unassigned'
        const locName =
          claim.location?.name || t('claimsPage.unassignedLocation')

        if (!locationBuckets[locId]) {
          locationBuckets[locId] = {
            companyId: result.data.companyId,
            locationId: locId,
            locationName: locName,
            claims: [],
          }
        }

        locationBuckets[locId].claims.push(claim)
      })

      claimsMap[companyKey] = Object.values(locationBuckets).sort((a, b) => {
        if (a.locationId === 'unassigned') return 1
        if (b.locationId === 'unassigned') return -1
        return 0
      })
    }
  })

  return {
    claimsMap,
    isLoading: results.some((result) => result.isLoading),
    isFetching: results.some((result) => result.isFetching),
    isError: results.some((result) => result.isError),
  }
}
